import { LL } from 'i18n/i18n.service';
import { AuthService } from 'services/auth.service';
import { getMyApp } from 'app/app-interface';
import { NavController } from 'providers/nav-controller';
import { LoggingService } from 'services/logging.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ILoginPage } from 'pages/login/login-interface';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { getRootDomain } from 'environments/environment-helpers';
import { classifyCheckoutFrameMessage } from './subscribe-frame-message-filter';
import { PlanOption } from '../../services/paypro-price-fetcher.service';
import { CheckoutEmailVerdict } from './checkout-email-verdict';

/** What the rail borrows from the page it is rendering inside. */
export interface PayProRailHost {
  /** Mark the page dirty — the app is zoneless, so a model change alone repaints nothing. */
  markPageForCheck(): void;
  /** The page's `safeTimeout`: cancelled on teardown, and skipped once the view is gone. */
  scheduleOnPage(callback: () => void, delayMs: number): void;
  /** Show or hide the page's checkout spinner. */
  setLoadingCheckout(loading: boolean): void;
  /**
   * Read that same spinner back. Only the unrecognised-message report below needs
   * it: knowing whether the checkout was still loading when an unknown message
   * arrived is what tells the reader which stage of the checkout produced it.
   */
  isLoadingCheckout(): boolean;
  /** The shared email-conflict verdict; the Paddle rail asks the same question. */
  classifyCheckoutEmailAsync(email: string): Promise<CheckoutEmailVerdict>;
}

/** The services the rail calls out to. The page injects them and hands them over. */
export interface PayProRailServices {
  auth: AuthService;
  logging: LoggingService;
  alertCtrl: AlertController;
  navCtrl: NavController;
  sanitizer: DomSanitizer;
}

/**
 * The PayPro (PPG) checkout rail of the subscribe page, split out of the page
 * class — deferred finding `df-f3f664504bca`, ticket #360, the second of that
 * page's three payment rails to move.
 *
 * PayPro's checkout is a plain `<iframe>` the page renders from a buy URL. The
 * frame talks back to us with `window.postMessage`: it reports its height so we
 * can size the iframe, and it reports the email the buyer typed so we can refuse
 * a purchase that would collide with an existing account.
 *
 * ## Why this is a plain class and not an Angular service
 *
 * The same reason the Paddle rail is one (see `subscribe-paddle-rail.ts`):
 * everything here belongs to ONE visit to the subscribe page — which buy URL is
 * loaded, how tall the frame is, whether our message listener is attached. An
 * `@Injectable()` service is a singleton, so that state would survive the page and
 * leak into the next visit. The page constructs one of these and drops it with
 * itself.
 *
 * ## What it does NOT own
 *
 * Everything the other two rails also touch stays on the page: which web rail is
 * active, the plan list the shared selector renders, the checkout spinner, and the
 * email-conflict check — which the Paddle rail runs too, so the rail reaches it
 * through {@link PayProRailHost} rather than owning it.
 *
 * The native Apple / Google Play rail has since moved out too, into
 * `subscribe-native-rail.ts`. Why it moved last, and why a change to it wants a
 * person with a real device, is in `design-docs/paddle-integration.md`.
 */
export class SubscribePayProRail {
  // the sanitized buy URL the template feeds to the checkout iframe's src.
  // undefined until a plan is selected, which is what the template gates on.
  private buyUrl: SafeResourceUrl = undefined;
  // the iframe's height in pixels. The frame reports its own height when it loads
  // and again whenever it resizes; 600 is the starting guess before it speaks.
  private frameHeight = 600;
  // tracks whether the iframe-message listener is currently attached to window.
  // Gates the idempotent attach in enterPage and the matching detach in teardown
  // so re-entering the page does not accumulate duplicate handlers.
  private listenerAttached = false;

  constructor(
    private readonly services: PayProRailServices,
    private readonly host: PayProRailHost
  ) {}

  /** The buy URL for the template's iframe, or undefined before a plan is picked. */
  get currentBuyUrl(): SafeResourceUrl {
    return this.buyUrl;
  }

  /** How tall the template should render the checkout iframe, in pixels. */
  get checkoutFrameHeight(): number {
    return this.frameHeight;
  }

  /**
   * Entering the page: start the return-host cookie refresh and attach the
   * iframe-message listener.
   *
   * The attach is idempotent against re-entry — {@link messageListener} is an
   * arrow property so its identity is stable, and we additionally track attachment
   * state to avoid accumulating duplicate listeners that would each call
   * markForCheck on stale SubscribePage instances after navigation away and back.
   */
  enterPage(): void {
    this.refreshReturnHostCookie();
    if (!this.listenerAttached) {
      window.addEventListener('message', this.messageListener, false);
      this.listenerAttached = true;
    }
  }

  /**
   * Leaving the page: detach the iframe-message listener attached in
   * {@link enterPage}.
   *
   * Without this, re-entering the page would accumulate handlers; each one would
   * call markForCheck on the now-stale page instance on the next PayPro
   * postMessage, surfacing as an NG0911 in Sentry.
   */
  teardown(): void {
    if (this.listenerAttached) {
      window.removeEventListener('message', this.messageListener, false);
      this.listenerAttached = false;
    }
  }

  /**
   * The buyer picked a plan: point the iframe at that plan's buy URL and show the
   * spinner until the frame reports it has loaded.
   */
  selectPlan(opt: PlanOption): void {
    this.buyUrl = this.services.sanitizer.bypassSecurityTrustResourceUrl(opt.buyUrl);
    this.host.setLoadingCheckout(true);
  }

  // store the current hostname so the app can redirect back here after PayPro's
  // checkout-complete redirect (which may land on a different subdomain). refresh
  // every 60s so the 10-minute TTL does not expire while the user is filling in
  // payment details.
  private refreshReturnHostCookie(): void {
    document.cookie = `ppg_return_host=${location.host};domain=.${getRootDomain()};path=/;max-age=600;secure;samesite=lax`;
    this.host.scheduleOnPage(() => this.refreshReturnHostCookie(), 60000);
  }

  private messageListener = (ev: { data: unknown, origin: string }) => {
    // dispatch is driven by classifyCheckoutFrameMessage — see that module
    // for the origin / ignore-pattern / parse rules and the Sentry 3S9 fix.
    const result = classifyCheckoutFrameMessage(ev);

    switch (result.kind) {
      case 'ignore':
        return;

      case 'unrecognised':
        // ev is guaranteed defined here — classifyCheckoutFrameMessage returns
        // { kind: 'ignore' } for null/undefined events, so reaching this arm
        // means ev passed the origin check too.
        this.services.logging.trackSilentProblem('UnexpectedCheckoutFrameMessage', {
          data: result.data.substring(0, 500),
          origin: ev.origin,
          loadingCheckout: this.host.isLoadingCheckout(),
          checkoutFrameHeight: this.frameHeight,
        });
        return;

      case 'parseError':
        this.services.logging.trackAbnormalException(result.error, LL.subscribe.errors.unexpectedCheckout());
        this.host.markPageForCheck();
        return;

      case 'event': {
        const event = result.event;
        if (event.type === 'checkout_loaded') {
          console.log('PayPro: Checkout frame loaded');
          this.frameHeight = event.height;
          this.host.setLoadingCheckout(false);
        } else if (event.type === 'checkout_resized') {
          console.log(`PayPro: Checkout frame resized to ${event.height}`);
          this.frameHeight = event.height;
        } else if (event.type === 'email_changed') {
          console.log('PayPro: Email changed');
          void this.handleEmailChangedAsync(event);
          /*
           these are no longer possible since PPG is using redirect to the success/failure page
          } else if (event.type === 'checkout_succeeded') {
            console.log('PayPro: Checkout frame succeeded');
            void this.onCheckoutSuccessAsync();
          } else if (event.type === 'checkout_review' || event.type === 'checkout_waiting') {
            this.logging.trackAbnormalError(ev.data, LL.errors.userFacing.paymentPending());
            void this.onCheckoutSuccessAsync();
          */
        } else {
          this.services.logging.trackSilentProblem('UnknownCheckoutFrameMessageType', {
            type: event.type,
            data: result.data.substring(0, 500),
            origin: ev.origin,
          });
        }
        this.host.markPageForCheck();
        return;
      }
    }
  };

  private sendMessageToCheckoutFrame(message: any) {
    const iframe = document.querySelector('iframe[src*="store.payproglobal.com"]') as HTMLIFrameElement;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(JSON.stringify(message), 'https://store.payproglobal.com');
      console.log('PayPro: Sent message to checkout frame:', message);
    } else {
      console.log('PayPro: Could not find checkout iframe to send message');
    }
  }

  private async handleEmailChangedAsync(event: { value?: string }) {
    if (!event.value) {
      console.log('PayPro: Email changed event received but no email value provided');
      return;
    }

    const email = event.value;

    // basic email format check — skip server call for incomplete emails
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return;
    }

    console.log(`PayPro: Checking email ${email}`);

    // run the shared conflict check, then apply the PPG-specific post-action.
    // On 'ok' (logged-in match or a free email) we hand the validation back to
    // the PPG frame so it lets the buyer continue; on a conflict we alert and
    // divert exactly as before.
    const verdict = await this.host.classifyCheckoutEmailAsync(email);
    switch (verdict) {
      case 'ok':
        console.log('PayPro: Email validated, sending validation message');
        this.sendMessageToCheckoutFrame({ type: 'email_validated' });
        return;
      case 'mismatch':
        console.log(`PayPro: Email mismatch - user email: ${this.services.auth.user?.email}, checkout email: ${email}`);
        await this.services.alertCtrl.showAlertAsync({
          title: LL.subscribe.alerts.emailMismatch.title(),
          message: LL.subscribe.alerts.emailMismatch.message(),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
        });
        // reload the page to reset the checkout
        void getMyApp().reloadAppAsync({ resetToRoot: false });
        return;
      case 'exists':
        await this.services.alertCtrl.showAlertAsync({
          title: LL.subscribe.alerts.accountExists.title(),
          message: LL.subscribe.alerts.accountExists.message(),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
        });
        // redirect to sign-in via email with auto-send magic link
        void this.services.navCtrl.setRootAsync(ILoginPage, { mode: 'login', email: email, autoSendLink: true });
        return;
      case 'error':
        await this.services.alertCtrl.showAlertAsync({
          title: LL.subscribe.alerts.cantVerifyEmail.title(),
          messageMarkdown: LL.subscribe.alerts.cantVerifyEmail.message(),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
        });
        // restart the app to reset the checkout
        void getMyApp().reloadAppAsync({ resetToRoot: false });
        return;
    }
  }
}
